// Helpers for rendering AnalysisScore blocks (the canonical
// before/after struct returned by every tree-optimisation analyser).

export const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

const UNIT_FORMATTERS = {
  '%': (value) => `${value.toFixed(1)}%`,
  'mW': (value) => {
    if (value >= 1000) return `${(value / 1000).toFixed(2)} W`;
    if (value >= 1) return `${value.toFixed(1)} mW`;
    if (value === 0) return '0 mW';
    return `${value.toPrecision(2)} mW`;
  },
  '0\u2013100': (value) => value.toFixed(1),
  '0-100': (value) => value.toFixed(1),
  cost: (value) => value.toFixed(1),
  'severity': (value) => value.toFixed(2),
};

// formatScoreValue picks a sensible printer based on `unit`.
// Unknown units get a 3-significant-digit fallback so we don't
// leak floating-point noise into the UI.
export const formatScoreValue = (value, unit) => {
  if (!isFiniteNumber(value)) return '—';
  const formatter = UNIT_FORMATTERS[unit];
  if (formatter) return formatter(value);
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toPrecision(3);
};

export const formatPct = (value) => {
  if (!isFiniteNumber(value)) return null;
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Direction-aware "is this delta good?" check.
//   higher_is_better -> positive delta = good
//   lower_is_better  -> negative delta = good
export const deltaIsImprovement = (score) => {
  if (!score || !isFiniteNumber(score.delta)) return null;
  if (score.delta === 0) return null;
  if (score.direction === 'higher_is_better') return score.delta > 0;
  return score.delta < 0;
};

// MUI severity color hint to use for chips/alerts.
export const scoreToneColor = (score) => {
  if (!score) return 'default';
  if (score.is_advisory) {
    // Advisory scores: use the absolute value (0–100 health-style)
    // to colour. We treat >=80 great, >=60 ok, otherwise warning.
    if (!isFiniteNumber(score.before)) return 'default';
    if (score.before >= 80) return 'success';
    if (score.before >= 60) return 'info';
    if (score.before >= 40) return 'warning';
    return 'error';
  }
  const improved = deltaIsImprovement(score);
  if (improved === true) return 'success';
  if (improved === false) return 'warning';
  return 'default';
};

// Concrete metric labels for known analysers, with a sensible
// fallback to the score's own `metric_label` field.
export const metricHeading = (score, fallback) => {
  if (score?.metric_label) return score.metric_label;
  if (score?.metric) return score.metric;
  return fallback || 'Score';
};
